import {
	Alert,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	Tabs,
	Typography,
} from 'antd';
import {
	ArrowLeftOutlined,
	FileSearchOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { FC, Fragment, useState } from 'react';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow from '@/libs/antd/components/DataRow';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	MPPManualSLIKMainInfo,
	MPPManualSLIKResult,
} from '@/libs/fleet-api/services/mpp';
import { useModals } from '@/components/modals/ModalProvider';

const AnalisaSLIKManual: FC<{
	result: MPPManualSLIKResult;
	mainInformation?: MPPManualSLIKMainInfo;
	notes: string;
	onBack?: () => void;
}> = ({ onBack, result, mainInformation, notes }) => {
	const {
		openPreviewModal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openProspectDetail,
	} = useModals();
	const [activeTab, setActiveTab] = useState('manager');
	const {
		managementManualSliks,
		shareholderManualSliks,
		slikFile,
		slikResult,
	} = result;

	const {
		customerId,
		customerName,
		prospectId,
		customerGroup,
		customerGroupId,
	} = mainInformation ?? {};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>Main Information</FleetTitle>
				</div>
			</div>

			<DataRow
				style={{ marginBottom: 20 }}
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName ?? '-'}
								</a>
							) : (
								'-'
							),
						],
						[
							'Prospect ID',
							prospectId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openProspectDetail(prospectId);
									}}
								>
									{prospectId ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.portofolioGuideline ?? '-',
						],
						['Segmen Market', mainInformation?.industrySector ?? '-'],
					],
					[
						['NPWP', mainInformation?.npwp ?? '-'],
						[
							'Group Customer BMPP',
							customerGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(customerGroupId);
									}}
								>
									{customerGroup ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
				]}
			/>

			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<Typography.Title level={3}>SLIK Information</Typography.Title>
				<Alert
					type="info"
					message={
						<strong style={{ color: '#1557BC' }}>
							Maaf, sistem robotik sedang dalam proses pemeliharaan. Silakan
							unggah file SLIK PDF sebagai alternatif.
						</strong>
					}
				/>
				<Form {...ANTD_DEFAULT_PROPS.FORM}>
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="View SLIK">
								{slikFile ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(slikFile);
										}}
									>
										{slikFile.name ?? 'View Dokumen'}
									</a>
								) : (
									'-'
								)}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="SLIK Result">
								<Input
									disabled
									value={slikResult}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />

					<Tabs
						activeKey={activeTab}
						onChange={(key) => setActiveTab(key)}
						items={[
							{
								key: 'manager',
								label: 'Pengurus',
								children: (
									<Table
										bordered
										style={{ marginBottom: 20 }}
										pagination={false}
										dataSource={
											managementManualSliks?.map((record, key) => {
												return {
													...record,
													key,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'View Detail',
												width: 110,
												align: 'center',
												render: () => {
													return (
														<a
															href="#"
															style={{ color: '#bbb' }}
															onClick={(e) => {
																e.preventDefault();
															}}
														>
															<FileSearchOutlined />
														</a>
													);
												},
											},
											{
												title: 'View Ideb',
												width: 110,
												align: 'center',
												render: (_, record) => {
													if (!record.idebFile) {
														return (
															<FileSearchOutlined style={{ color: '#bbb' }} />
														);
													}

													return (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openPreviewModal(record.idebFile);
															}}
														>
															<FileSearchOutlined />
														</a>
													);
												},
											},
											{
												title: 'Upload Ideb',
												width: 110,
												align: 'center',
												render: () => {
													return (
														<a
															href="#"
															style={{ color: '#bbb' }}
															onClick={(e) => {
																e.preventDefault();
															}}
														>
															<UploadOutlined />
														</a>
													);
												},
											},
											{
												title: 'Nama Pengurus',
												width: 300,
												render: (_, record) => record.name ?? '-',
											},
											{
												title: 'SLIK Result',
												render: (_, record) => {
													return (
														<Fragment>
															<Select
																size="small"
																style={{ width: 80 }}
																disabled
																value={record.slikResult}
															/>
														</Fragment>
													);
												},
											},
										]}
									/>
								),
							},
							{
								key: 'shareholder',
								label: 'Pemegang Saham',
								children: (
									<Table
										bordered
										style={{ marginBottom: 20 }}
										pagination={false}
										dataSource={
											shareholderManualSliks?.map((record, key) => {
												return {
													...record,
													key,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'View Detail',
												width: 110,
												align: 'center',
												render: () => {
													return (
														<a
															href="#"
															style={{ color: '#bbb' }}
															onClick={(e) => {
																e.preventDefault();
															}}
														>
															<FileSearchOutlined />
														</a>
													);
												},
											},
											{
												title: 'View Ideb',
												width: 110,
												align: 'center',
												render: (_, record) => {
													if (!record.idebFile) {
														return (
															<FileSearchOutlined style={{ color: '#bbb' }} />
														);
													}

													return (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openPreviewModal(record.idebFile);
															}}
														>
															<FileSearchOutlined />
														</a>
													);
												},
											},
											{
												title: 'Upload Ideb',
												width: 110,
												align: 'center',
												render: () => {
													return (
														<a
															href="#"
															style={{ color: '#bbb' }}
															onClick={(e) => {
																e.preventDefault();
															}}
														>
															<UploadOutlined />
														</a>
													);
												},
											},
											{
												title: 'Nama Pemegang Saham',
												width: 300,
												render: (_, record) => record.name ?? '-',
											},
											{
												title: 'SLIK Result',
												render: (_, record) => {
													return (
														<Fragment>
															<Select
																size="small"
																style={{ width: 80 }}
																disabled
																value={record.slikResult}
															/>
														</Fragment>
													);
												},
											},
										]}
									/>
								),
							},
						]}
					/>

					<Form.Item
						label="Catatan"
						labelCol={{
							span: 6,
						}}
						wrapperCol={{
							span: 18,
						}}
					>
						<Input.TextArea
							disabled
							rows={4}
							value={notes}
						/>
					</Form.Item>
				</Form>
			</Space>
		</Space>
	);
};

export default AnalisaSLIKManual;
